import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

export interface FlowNode {
  id: string;
  type: 'message' | 'question' | 'condition' | 'action' | 'tool';
  label: string;
  data: any;
  position: { x: number; y: number };
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

interface Flow {
  id: string;
  name: string;
  description?: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  status: 'DRAFT' | 'PUBLISHED';
  version: number;
  assistantId?: string;
}

interface BuilderState {
  flow: Flow | null;
  flows: Flow[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  selectedNode: FlowNode | null;
}

const initialState: BuilderState = {
  flow: null,
  flows: [],
  loading: false,
  saving: false,
  error: null,
  selectedNode: null,
};

// Async thunks
export const fetchFlows = createAsyncThunk(
  'builder/fetchFlows',
  async (assistantId: string) => {
    const response = await api.get(`/assistants/${assistantId}/flows`);
    return response.data.flows;
  }
);

export const fetchFlow = createAsyncThunk(
  'builder/fetchFlow',
  async (flowId: string) => {
    const response = await api.get(`/flows/${flowId}`);
    return response.data.flow;
  }
);

export const createFlow = createAsyncThunk(
  'builder/createFlow',
  async ({ assistantId, name, description }: { assistantId: string; name: string; description?: string }) => {
    const response = await api.post('/flows', { assistantId, name, description });
    return response.data.flow;
  }
);

export const updateFlow = createAsyncThunk(
  'builder/updateFlow',
  async ({ flowId, nodes, edges, metadata }: { 
    flowId: string; 
    nodes: FlowNode[]; 
    edges: FlowEdge[]; 
    metadata?: any 
  }) => {
    const response = await api.put(`/flows/${flowId}`, { nodes, edges, metadata });
    return response.data.flow;
  }
);

export const publishFlow = createAsyncThunk(
  'builder/publishFlow',
  async (flowId: string) => {
    const response = await api.post(`/flows/${flowId}/publish`);
    return response.data.flow;
  }
);

export const deleteFlow = createAsyncThunk(
  'builder/deleteFlow',
  async (flowId: string) => {
    await api.delete(`/flows/${flowId}`);
    return flowId;
  }
);

export const generateFlowFromDescription = createAsyncThunk(
  'builder/generateFlowFromDescription',
  async ({ assistantId, description }: { assistantId: string; description: string }) => {
    const response = await api.post('/flows/generate', { assistantId, description });
    return response.data;
  }
);

const builderSlice = createSlice({
  name: 'builder',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectNode: (state, action: PayloadAction<FlowNode | null>) => {
      state.selectedNode = action.payload;
    },
    updateNodeData: (state, action: PayloadAction<{ nodeId: string; data: any }>) => {
      if (state.flow) {
        const node = state.flow.nodes.find(n => n.id === action.payload.nodeId);
        if (node) {
          node.data = { ...node.data, ...action.payload.data };
        }
      }
    },
    addNode: (state, action: PayloadAction<FlowNode>) => {
      if (state.flow) {
        state.flow.nodes.push(action.payload);
      }
    },
    removeNode: (state, action: PayloadAction<string>) => {
      if (state.flow) {
        state.flow.nodes = state.flow.nodes.filter(n => n.id !== action.payload);
        state.flow.edges = state.flow.edges.filter(
          e => e.source !== action.payload && e.target !== action.payload
        );
      }
    },
    addEdge: (state, action: PayloadAction<FlowEdge>) => {
      if (state.flow) {
        state.flow.edges.push(action.payload);
      }
    },
    removeEdge: (state, action: PayloadAction<string>) => {
      if (state.flow) {
        state.flow.edges = state.flow.edges.filter(e => e.id !== action.payload);
      }
    },
    updateNodePosition: (state, action: PayloadAction<{ nodeId: string; position: { x: number; y: number } }>) => {
      if (state.flow) {
        const node = state.flow.nodes.find(n => n.id === action.payload.nodeId);
        if (node) {
          node.position = action.payload.position;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch flows
      .addCase(fetchFlows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlows.fulfilled, (state, action) => {
        state.loading = false;
        state.flows = action.payload;
      })
      .addCase(fetchFlows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch flows';
      })
      
      // Fetch flow
      .addCase(fetchFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.flow = action.payload;
      })
      .addCase(fetchFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch flow';
      })
      
      // Create flow
      .addCase(createFlow.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createFlow.fulfilled, (state, action) => {
        state.saving = false;
        state.flows.push(action.payload);
        state.flow = action.payload;
      })
      .addCase(createFlow.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to create flow';
      })
      
      // Update flow
      .addCase(updateFlow.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateFlow.fulfilled, (state, action) => {
        state.saving = false;
        state.flow = action.payload;
        
        // Update in flows list
        const index = state.flows.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.flows[index] = action.payload;
        }
      })
      .addCase(updateFlow.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to update flow';
      })
      
      // Publish flow
      .addCase(publishFlow.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(publishFlow.fulfilled, (state, action) => {
        state.saving = false;
        state.flow = action.payload;
        
        // Update in flows list
        const index = state.flows.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.flows[index] = action.payload;
        }
      })
      .addCase(publishFlow.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to publish flow';
      })
      
      // Delete flow
      .addCase(deleteFlow.fulfilled, (state, action) => {
        state.flows = state.flows.filter(f => f.id !== action.payload);
        if (state.flow?.id === action.payload) {
          state.flow = null;
        }
      })
      
      // Generate flow from description
      .addCase(generateFlowFromDescription.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(generateFlowFromDescription.fulfilled, (state, action) => {
        state.saving = false;
        state.flow = action.payload.flow;
        state.flows.push(action.payload.flow);
      })
      .addCase(generateFlowFromDescription.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to generate flow';
      });
  },
});

export const {
  clearError,
  selectNode,
  updateNodeData,
  addNode,
  removeNode,
  addEdge,
  removeEdge,
  updateNodePosition,
} = builderSlice.actions;

export default builderSlice.reducer;